/**
 * In-memory ring buffer of recent user actions / navigations / fetch
 * outcomes. Attached to error reports so the support team sees what
 * the user did in the seconds leading up to a failure.
 *
 * Memory-only and tab-scoped: no PII persists across reloads.
 */

export interface Breadcrumb {
    /** ISO timestamp (truncated to the second to keep payloads small). */
    t: string;
    /** Short category — keep stable so log queries can group on it. */
    category: 'navigation' | 'fetch' | 'click' | 'auth' | 'error' | 'csp';
    /** One-line summary, no PII beyond pathname / status code. */
    message: string;
    /** Optional small structured detail (≤ 200 chars when stringified). */
    data?: Record<string, string | number | boolean | null>;
}

const MAX_BREADCRUMBS = 25;
const buffer: Breadcrumb[] = [];

export function addBreadcrumb(crumb: Omit<Breadcrumb, 't'>): void {
    buffer.push({
        t: new Date().toISOString().slice(0, 19) + 'Z',
        ...crumb,
    });
    if (buffer.length > MAX_BREADCRUMBS) {
        buffer.splice(0, buffer.length - MAX_BREADCRUMBS);
    }
}

/**
 * Snapshot the buffer for inclusion in an error report. Returns a shallow
 * copy so callers can stringify without seeing later mutations.
 */
export function getBreadcrumbs(): Breadcrumb[] {
    return buffer.slice();
}

export function clearBreadcrumbs(): void {
    buffer.length = 0;
}
